import React from 'react';
import Button from '@material-ui/core/Button';
import isEmpty from 'lodash/isEmpty';
import confirm from 'utils/confirm';
import {browserHistory, Link} from 'react-router';
import FieldContainer from 'components/pure/utils/form/FieldContainer';
import InputField from 'components/pure/utils/form/InputField';
import {List} from 'immutable';
import CheckboxField from 'components/pure/utils/form/CheckboxField';
import ClickableHelpIcon from 'components/pure/utils/ClickableHelpIcon';
import './ReviewTypeEditPage.styl';
import ReviewTypeQuestions from 'components/pure/admin/ReviewTypeQuestions';
import {redistributeWeights, validate} from 'records/CoachingReviewType';
import noop from 'lodash/noop';
import CoachingReviewTypeActions from 'actions/coaching/CoachingReviewTypeActions';
import {ReviewTypeDeleteConfirm} from 'components/pure/admin/ReviewTypeDeleteConfirm';
import FlatButton from 'components/pure/utils/FlatButton';
import ReviewTypeBreadcrumbs from 'components/pure/admin/ReviewTypeBreadcrumbs';
import ToastStore from '@store/toast/ToastStore';
import SelectField from 'components/pure/utils/form/SelectField';
import CoachingReviewQuestion from 'records/CoachingReviewTypeQuestion';
import {MeetingCategoriesDropdown} from 'components/dashboard/common/MeetingCategoriesDropdown';
import WeightingInput from 'components/pure/admin/WeightingInput';

const isDefaultLabel = `Set this meeting type as the default for all coaching meetings`;
const includesCallsLabel = `Include call review in coaching meetings of this type`;
const isDefaultHelp = () =>
  `Only one meeting type can be the default at a time. Future coaching plans will use this as the meeting type unless otherwise selected.`;
const includesCallHelp = () =>
  `If checked, calls will be presented during coaching meetings for discussion, in addition to any questions you list below. If unchecked, coaching meetings of this type will be focused on your questions only.`;

export default class ReviewTypeEditPage extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      model: props.type,
      errors: {},
      showErrors: false,
      blinkCallWeighting: undefined,
      questionsToBlink: List(),
      // list of questions user has edited, for ex [0, 1] if the user has already
      // edited the first two
      questionsUserHasEdited: List(),
      hasEditedCallWeights: false,
    };

    this.onChange = this.onChange.bind(this);
    this.onDelete = this.onDelete.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.updateHints = this.updateHints.bind(this);
    this.onAddNewQuestion = this.onAddNewQuestion.bind(this);
    this.onDeleteQuestion = this.onDeleteQuestion.bind(this);
    this.onMoveQuestion = this.onMoveQuestion.bind(this);
    this.defaultMeetingCategory = this.defaultMeetingCategory.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!this.props.type.equals(nextProps.type)) {
      // We got a type from our store, overwrite the existing state
      this.setState({model: nextProps.type});
    }
  }

  onDelete() {
    confirm(<ReviewTypeDeleteConfirm />).then(() => {
      CoachingReviewTypeActions.deleteReviewType(this.state.model.id).then(() =>
        browserHistory.push('/admin/sessionTypes')
      );
    }, noop);
  }

  onSubmit() {
    const errors = validate(this.state.model);

    const defaultMeetingCategory = this.defaultMeetingCategory();

    if (!isEmpty(errors)) {
      this.setState({
        showErrors: true,
        errors,
      });
    } else {
      const model = this.state.model
        .update('callWeighting', w => (this.state.model.includesCalls ? w : 0))
        .update('questions', questions => questions.map(q => (q.isText() ? q.set('weight', 0) : q)))
        .update('meetingCategoryId', meetingCategoryId =>
          Number.isInteger(this.state.model.meetingCategoryId)
            ? meetingCategoryId
            : defaultMeetingCategory?.id
        );
      CoachingReviewTypeActions.upsertReviewType(model).then(() =>
        browserHistory.push({
          pathname: '/admin/sessionTypes',
          state: {submit: true},
        })
      );
    }
  }

  onChange(changes) {
    const {onChange, type} = this.props;
    const model = this.state.model.merge(changes);
    const errors = validate(model);
    this.setState({
      model,
      errors,
    });
    onChange(model.equals(type));
  }

  /**
   * redistribute weights based on whether or not the last action taken was
   * a question or updating call weight.
   *
   * @param updatedQuestion
   * @param updatedCallWeight
   * @param qIdx - question index
   */
  onUpdateWeighting(updatedQuestion, updatedCallWeight, qIdx) {
    const {model, questionsUserHasEdited, hasEditedCallWeights} = this.state;

    const updatedQuestionEditTally = updatedQuestion
      ? questionsUserHasEdited.push(qIdx)
      : questionsUserHasEdited;
    const updatedEditedCallWeight = updatedCallWeight !== null || hasEditedCallWeights;

    const [updated, blowAwayEditTally] = redistributeWeights(
      model,
      updatedQuestionEditTally,
      updatedEditedCallWeight,
      updatedCallWeight !== null,
      qIdx
    );

    this.updateHints(model, updated);

    this.onChange(updated);

    // if we couldn't preserve user's choices during redistribution,
    // we scaled the rest except for the last update proportionally.
    // Keep the last edit as the new tally list for what the user has officially edited.
    this.setState({
      questionsUserHasEdited: blowAwayEditTally
        ? qIdx
          ? List([qIdx])
          : List()
        : updatedQuestionEditTally,
      hasEditedCallWeights: blowAwayEditTally
        ? updatedCallWeight !== null
        : updatedEditedCallWeight,
    });
  }

  onAddNewQuestion() {
    const {model: oldModel, questionsUserHasEdited, hasEditedCallWeights} = this.state;

    // try to guess at a "fair" weight for the new question given buckets we'll
    // later redistribute and amt of qs, etc
    const totalBuckets =
      oldModel.questions.filter(q => !questionsUserHasEdited.contains(q) && !q.isText()).size +
      1 +
      (oldModel.includesCalls ? 1 : 0);

    const newQuestionFairlyWeighted = new CoachingReviewQuestion({
      weight: totalBuckets === 0 ? 100 : Math.round(100 / totalBuckets),
    });

    const withNewQuestion = oldModel.set(
      'questions',
      oldModel.questions.push(newQuestionFairlyWeighted)
    );

    const [updated, blowAwayEditTally] = redistributeWeights(
      withNewQuestion,
      questionsUserHasEdited,
      hasEditedCallWeights,
      false,
      // also if we need to blow away edit tally, preserve new q
      withNewQuestion.size
    );

    this.updateHints(withNewQuestion, updated);

    this.onChange(updated);

    if (blowAwayEditTally) {
      this.setState({
        questionsUserHasEdited: List(),
        hasEditedCallWeights: false,
      });
    }
  }

  onMoveQuestion(from, to) {
    const {model, questionsUserHasEdited} = this.state;
    const movedQuestion = model.questions.get(from);
    const questions = model.questions.remove(from).insert(to, movedQuestion);

    // did we go up or down?
    const questionMovedUp = from - to > 0;

    // add all the qs that we can unaffected by the move
    let newEditedQuestions = [];

    // conditionally add the moved q
    if (questionsUserHasEdited.contains(from)) {
      newEditedQuestions.push(to);
    }

    // if any of the rest were edited, they've moved up or down one position. so track that too
    const toEdit = questionMovedUp
      ? questionsUserHasEdited.filter(i => i !== from).map(i => (i >= to && i < from ? i + 1 : i))
      : questionsUserHasEdited.filter(i => i !== from).map(i => (i > from && i <= to ? i - 1 : i));

    newEditedQuestions.push(...toEdit);

    this.setState({
      questionsUserHasEdited: List(newEditedQuestions).sort((a, b) => a - b),
    });

    this.onChange({questions});
  }

  onDeleteQuestion(index) {
    const {model, questionsUserHasEdited, hasEditedCallWeights} = this.state;

    const updatedQuestionEditTally = questionsUserHasEdited
      .filter(i => i !== index)
      .map(i => (i > index ? i - 1 : i));

    const updatedModel = model.set('questions', model.questions.remove(index));

    this.setState({
      // because we deleted a question, move the subsequent ones up
      questionsUserHasEdited: updatedQuestionEditTally,
    });

    const [updatedWeights] = redistributeWeights(
      updatedModel,
      updatedQuestionEditTally,
      hasEditedCallWeights
    );

    this.updateHints(model, updatedWeights);

    this.onChange(updatedWeights);
  }

  updateIncludesCalls(includesCalls) {
    const {model, questionsUserHasEdited, hasEditedCallWeights} = this.state;

    const [updated] = redistributeWeights(
      model.merge({includesCalls, callWeighting: includesCalls ? 100 : 0}),
      questionsUserHasEdited,
      hasEditedCallWeights
    );

    this.updateHints(model, updated);

    this.onChange(updated);
  }

  /**
   * Given a potentially redistributed model, add UI hints to make it obvious
   * to the user that we've updated the weights.
   *
   * Blink the updated weights (we do this by adding a date key and a css animation,
   * the key ensures subsequent blinks force rerenders) and toast
   *
   * @param model - the old model
   * @param updated - the redistributed model
   */
  updateHints(model, updated) {
    const shouldBlinkCallWeighting = updated.callWeighting !== model.callWeighting;

    const questionsToBlink = updated.questions.map((q, i) => {
      const originalQ = model.questions.get(i);

      return originalQ.weight !== q.weight ? new Date() : undefined;
    });

    this.setState({
      blinkCallWeighting: shouldBlinkCallWeighting ? new Date() : undefined,
      questionsToBlink,
    });

    if (shouldBlinkCallWeighting || questionsToBlink.filter(q => q).size > 0) {
      ToastStore.addToast({
        type: 'success',
        time: 4000,
        message: 'Automatically redistributing weights.',
      });
    }
  }

  /**
   * prepopulate the meeting category chooser with the "default" meeting category.
   *
   * However, orgs can technically hide their default category (call skill development) -
   * which is fine, in that case just show one of their visible categories
   *
   */
  defaultMeetingCategory() {
    const {meetingCategories} = this.props;

    if (!meetingCategories) return undefined;

    const visibleCats = meetingCategories.valueSeq().filter(mc => !mc.hidden);

    const theDefault = visibleCats.find(mc => mc.isDefault);

    return !!theDefault ? theDefault : visibleCats.first();
  }

  render() {
    const {meetingCategories, type, otherTypesWithCallsExist} = this.props;

    if (!meetingCategories) return null;

    const defaultMeetingCategory = this.defaultMeetingCategory();

    const meetingCategoryId = Number.isInteger(this.state.model.meetingCategoryId)
      ? this.state.model.meetingCategoryId
      : defaultMeetingCategory?.id;

    /*
      Backend does not allow deletes of:
        1. The default type.
        2. The last type that includes calls.
        3. Any type that is in use in an active coaching plan.
      Use type (the most recent version from the server) -- user's changes to the model are
      irrelevant here.
     */
    const canDelete =
      !type.isDefault &&
      (!type.includesCalls || otherTypesWithCallsExist) &&
      !type.isInUseByActivePlans;

    // Disallow a backdoor to the above -- can't uncheck the box on the last type with calls.
    const canNotUncheckCalls = this.state.model.includesCalls && !otherTypesWithCallsExist;

    return (
      <div className="session-type-edit-page">
        <ReviewTypeBreadcrumbs reviewType={this.state.model} />
        <h1>{this.props.newType ? 'New' : 'Edit'} Coaching Meeting Type</h1>
        <div className="type-details">
          <div className="top-row">
            <div className="name-wrapper">
              <FieldContainer
                error={this.state.showErrors && this.state.errors.name}
                className="name-label"
                label="Name">
                <InputField
                  value={this.state.model.name}
                  onChange={name => this.onChange({name})}
                  onBlur={() => this.onChange({name: this.state.model.name.trim()})}
                />
              </FieldContainer>
            </div>
            {meetingCategoryId && (
              <div className="category-wrapper">
                <FieldContainer className="meeting-category-label" label="Meeting category">
                  <MeetingCategoriesDropdown
                    meetingCategories={meetingCategories}
                    meetingCategoryId={meetingCategoryId}
                    onChange={({id: meetingCategoryId}) => this.onChange({meetingCategoryId})}
                    width={300}
                  />
                </FieldContainer>
              </div>
            )}
          </div>
          <div className="checkbox-container">
            <FieldContainer>
              <CheckboxField
                label={isDefaultLabel}
                checked={this.state.model.isDefault}
                onCheck={(_, checked) => this.onChange({isDefault: checked})}
              />
            </FieldContainer>
            <ClickableHelpIcon text={isDefaultHelp} />
          </div>
          <div className="checkbox-container">
            <FieldContainer>
              <CheckboxField
                label={includesCallsLabel}
                disabled={canNotUncheckCalls}
                checked={this.state.model.includesCalls}
                onCheck={(_, checked) => this.updateIncludesCalls(checked)}
              />
            </FieldContainer>
            <ClickableHelpIcon text={includesCallHelp} />
          </div>
        </div>
        <ReviewTypeQuestions
          onChange={questions => this.onChange({questions})}
          onWeightBlur={(question, idx) => this.onUpdateWeighting(question, null, idx)}
          onAddNewQuestion={this.onAddNewQuestion}
          onDeleteQuestion={this.onDeleteQuestion}
          onMoveQuestion={this.onMoveQuestion}
          questions={this.state.model.questions}
          errors={this.state.errors.questionErrors || List()}
          showErrors={this.state.showErrors}
          questionsToBlink={this.state.questionsToBlink}
          hasEditedCallWeights={this.state.hasEditedCallWeights}
          includesCalls={this.state.model.includesCalls}>
          {this.state.model.includesCalls && (
            <tr>
              <td className="reorder-cell" />
              <td className="question-type-cell">
                <FieldContainer>
                  <SelectField
                    hideResetButton
                    value={'Calls'}
                    disabled
                    options={[{label: 'Calls', value: 'Calls'}]}
                  />
                </FieldContainer>
              </td>
              <td className="question-cell call-score-wrapper">
                <span className="call-score-header">Score based on Call Review</span>
              </td>
              <td />
              <td />
              <td />
              <td className="weight-cell">
                <WeightingInput
                  inputKey={'call-weight-input-' + this.state.blinkCallWeighting}
                  val={this.state.model.callWeighting}
                  onChange={callWeighting => this.onChange({callWeighting: callWeighting || 0})}
                  onBlur={e => this.onUpdateWeighting(null, e.target.value)}
                  blink={this.state.blinkCallWeighting}
                />
              </td>
              <td />
            </tr>
          )}
        </ReviewTypeQuestions>
        {this.state.showErrors && (
          <div className="errors">
            <p className="field-error-message">{this.state.errors.noQuestions}</p>
            <p className="field-error-message">{this.state.errors.weightTooHigh}</p>
            <p className="field-error-message">{this.state.errors.weightTooLow}</p>
          </div>
        )}
        <div className="actions">
          <Button variant="contained" color="primary" className="ev-button" onClick={this.onSubmit}>
            Save
          </Button>
          <Button
            component={Link}
            variant="outlined"
            className="ev-button"
            to="/admin/sessionTypes">
            Cancel
          </Button>
          {canDelete && (
            <FlatButton
              className="delete-button"
              primary
              label="Delete Meeting Type"
              onClick={this.onDelete}
              style={{margin: '5px'}}
            />
          )}
        </div>
      </div>
    );
  }
}
